import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { getApp, getApps, initializeApp } from "firebase/app";
import { createUserWithEmailAndPassword, getAuth, signOut } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { ROLES, normalizeRole } from "../../utils/authRoles";
import { validateCreateUserForm } from "../../utils/validations";
import { useAuth } from "../../contexts/AuthContext";
import { setLoading } from "../../redux/loaderSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { TrashIcon } from "lucide-react";

const SECONDARY_AUTH_APP_NAME = "admin-create-user";

function getCreateUserAuth() {
  if (!getApps().length) {
    throw new Error("Firebase default app is not initialized.");
  }
  const existing = getApps().find((a) => a.name === SECONDARY_AUTH_APP_NAME);
  const secondaryApp = existing ?? initializeApp(getApp().options, SECONDARY_AUTH_APP_NAME);
  return getAuth(secondaryApp);
}

function getCreateUserErrorMessage(code) {
  switch (code) {
    case "auth/email-already-in-use":
      return "That email is already registered. Use a different email or update the existing user's role below.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Password is too weak. Use at least 6 characters.";
    case "auth/network-request-failed":
      return "Network error. Check your internet connection.";
    case "auth/operation-not-allowed":
      return "Email/password sign-in is disabled for this project in Firebase Authentication.";
    default:
      return "Unable to create user.";
  }
}

const Wrapper = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow};
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  h2 {
    margin: 0 0 4px 0;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 14px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border || '#eaeaea'};

  @media (max-width: 768px) {
    border: none;
    overflow-x: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#eaeaea'};
    font-size: 14px;
    word-break: break-word;
  }

  th {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: ${({ theme }) => theme.colors.background || '#f8f9fa'};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 600;
  }

  tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: block;

    thead {
      display: none;
    }

    tbody {
      display: block;
    }

    tr {
      display: block;
      margin-bottom: 16px;
      border: 1px solid ${({ theme }) => theme.colors.border || '#eaeaea'};
      border-radius: 8px;
      padding: 0;
      background: #fafafa;
    }

    td {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#eaeaea'};
      padding: 12px 16px;
    }

    td:last-child {
      border-bottom: none;
    }

    td::before {
      content: attr(data-label);
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textSecondary || '#666'};
      margin-bottom: 4px;
      letter-spacing: 0.5px;
    }
  }
`;

const RoleSelect = styled.select`
  padding: 8px;
  border: 1px solid ${({ hasError, theme }) => (hasError ? "#DC2626" : theme.colors.border)};
  border-radius: 6px;
`;

const Message = styled.p`
  margin-top: 12px;
  color: ${({ error, theme }) => (error ? theme.colors.danger : theme.colors.textSecondary)};
`;

const CreateForm = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 16px;
  max-width: 480px;

  @media (max-width: 500px) {
    gap: 12px;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ hasError, theme }) => (hasError ? "#DC2626" : theme.colors.border)};
  border-radius: 6px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ hasError, theme }) => (hasError ? "#DC2626" : theme.colors.accent || "#007bff")};
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const ErrorText = styled.div`
  color: #DC2626;
  font-size: 11px;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.accent || '#007bff'};
  color: ${({ theme }) => theme.colors.secondary || '#fff'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  width: fit-content;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;

  @media (max-width: 500px) {
    flex-direction: column;
    
    button {
      width: 100%;
      margin: 0 !important;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow || '0 4px 12px rgba(0,0,0,0.15)'};
  padding: 24px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-width: 500px) {
    padding: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
`;

const DeleteUserButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.danger};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: rgba(220, 53, 69, 0.08);
  }
`;

export default function UserRoles() {
  const { authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const loading = useSelector((state) => state.loader.loading);
  const [savingUid, setSavingUid] = useState("");
  const [deletingUid, setDeletingUid] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState(ROLES.SUB_ADMIN);
  const [creatingUser, setCreatingUser] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [createErrors, setCreateErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    const { errors: vErrors } = validateCreateUserForm({
      email: newEmail,
      password: newPassword,
      role: newRole,
    });

    const visibleErrors = Object.entries(vErrors).reduce((acc, [key, value]) => {
      if (touchedFields[key]) acc[key] = value;
      return acc;
    }, {});

    setCreateErrors(visibleErrors);
  }, [newEmail, newPassword, newRole, touchedFields]);

  const validateField = (key) => {
    setTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  const handleOpenModal = () => {
    setNewEmail("");
    setNewPassword("");
    setNewRole(ROLES.SUB_ADMIN);
    setTouchedFields({});
    setCreateErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const dispatch = useDispatch();

  const loadUsers = useCallback(async () => {
    dispatch(setLoading(true));

    try {
      const snapshot = await getDocs(collection(db, "users"));
      const mappedUsers = snapshot.docs
        .map((item) => ({
          id: item.id,
          ...item.data(),
          role: normalizeRole(item.data().role),
        }))
        .sort((a, b) => (a.email || "").localeCompare(b.email || ""));
      setUsers(mappedUsers);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load users.");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  }, [dispatch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateRole = async (userId, role) => {
    dispatch(setLoading(true));
    setSavingUid(userId);

    try {
      await setDoc(
        doc(db, "users", userId),
        {
          role,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setUsers((prevUsers) =>
        prevUsers.map((item) =>
          item.id === userId ? { ...item, role } : item
        )
      );
      toast.success("Role updated successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update role.");
    } finally {
      setSavingUid("");
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  const createUser = async (event) => {
    event.preventDefault();

    const validation = validateCreateUserForm({
      email: newEmail,
      password: newPassword,
      role: newRole,
    });

    if (!validation.isValid) {
      const markAllTouched = Object.keys(validation.errors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields((prev) => ({ ...prev, ...markAllTouched }));
      toast.error("Please fix form errors");
      return;
    }

    const email = newEmail.trim();
    const role = normalizeRole(newRole);

    dispatch(setLoading(true));
    setCreatingUser(true);

    try {
      const secondaryAuth = getCreateUserAuth();
      const credential = await createUserWithEmailAndPassword(secondaryAuth, email, newPassword);
      await signOut(secondaryAuth);

      const uid = credential.user.uid;
      const profileEmail = credential.user.email ?? email;

      await setDoc(
        doc(db, "users", uid),
        {
          uid,
          email: profileEmail,
          role,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setUsers((prev) => {
        const next = [
          ...prev.filter((u) => u.id !== uid),
          { id: uid, uid, email: profileEmail, role },
        ];
        return next.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
      });

      setNewEmail("");
      setNewPassword("");
      setNewRole(ROLES.SUB_ADMIN);
      toast.success("User created. They can sign in with this email and password.");
      handleCloseModal();
    } catch (error) {
      console.error(error);
      toast.error("Unable to create user.");
      const code = error?.code;
      if (code?.startsWith("auth/")) {
        toast.error(getCreateUserErrorMessage(code));
      } else if (code === "permission-denied") {
        toast.error("You do not have permission to save this user in Firestore.");
      } else {
        toast.error("Unable to save the user profile. If an Authentication account was still created, remove it in the Firebase console and try again.");
      }
    } finally {
      setCreatingUser(false);
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  const deleteUserProfile = async (userId, userEmail) => {
    if (userId === authUser?.uid) {
      toast.error("You cannot delete your own account from here.");
      return;
    }

    const label = userEmail || userId;
    const ok = window.confirm(
      `Remove this user's Firestore profile (${label})?`
    );
    if (!ok) return;

    setDeletingUid(userId);
    dispatch(setLoading(true));

    try {
      await deleteDoc(doc(db, "users", userId));
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success("User profile removed from Firestore.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete user profile.");
    } finally {
      setDeletingUid("");
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <Card>
          <h2>User Role Management</h2>
          <p>Loading users...</p>
        </Card>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* <Card style={{ marginTop: "60px", marginBottom: "16px" }}>
        <h2>Create user</h2>
        <p>
          Creates a Firebase Authentication account and a matching <code>users</code> document (same
          fields as after first login: <code>uid</code>, <code>email</code>, <code>role</code>).
        </p>
        <Hint>Uses a separate auth instance so you stay signed in as admin. Password must be at least 6 characters.</Hint>
        <PrimaryButton type="button" onClick={() => setIsModalOpen(true)}>
          Create user
        </PrimaryButton>
        {createMessage ? <Message error={createError}>{createMessage}</Message> : null}
      </Card> */}

      {isModalOpen ? (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3 style={{ margin: 0 }}>Create user</h3>
              <CloseButton aria-label="Close" onClick={handleCloseModal}>
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={createUser}>
              <CreateForm>
                <div>
                  <FieldLabel htmlFor="create-user-email">Email</FieldLabel>
                  <TextInput
                    id="create-user-email"
                    type="email"
                    placeholder="Enter email"
                    autoComplete="off"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      validateField("email");
                    }}
                    disabled={creatingUser}
                    hasError={!!createErrors.email}
                  />
                  {createErrors.email && <ErrorText>{createErrors.email}</ErrorText>}
                </div>
                <div>
                  <FieldLabel htmlFor="create-user-password">Password</FieldLabel>
                  <TextInput
                    id="create-user-password"
                    type="password"
                    placeholder="Enter password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      validateField("password");
                    }}
                    disabled={creatingUser}
                    hasError={!!createErrors.password}
                  />
                  {createErrors.password && <ErrorText>{createErrors.password}</ErrorText>}
                </div>
                <div>
                  <FieldLabel htmlFor="create-user-role">Role</FieldLabel>
                  <RoleSelect
                    id="create-user-role"
                    value={newRole}
                    onChange={(e) => {
                      setNewRole(e.target.value);
                      validateField("role");
                    }}
                    disabled={creatingUser}
                    hasError={!!createErrors.role}
                    style={{ width: "100%" }}
                  >
                    <option value={ROLES.ADMIN}>Admin</option>
                    <option value={ROLES.SUB_ADMIN}>Sub Admin</option>
                  </RoleSelect>
                  {createErrors.role && <ErrorText>{createErrors.role}</ErrorText>}
                </div>
                <ButtonGroup>
                  <PrimaryButton type="submit" disabled={creatingUser}>
                    {creatingUser ? "Creating…" : "Create user"}
                  </PrimaryButton>
                  <PrimaryButton
                    type="button"
                    style={{ background: "#e0e0e0", color: "#333" }}
                    onClick={handleCloseModal}
                    disabled={creatingUser}
                  >
                    Cancel
                  </PrimaryButton>
                </ButtonGroup>
              </CreateForm>
            </form>
          </ModalContent>
        </ModalOverlay>
      ) : null}

      <Card style={{ marginTop: "60px" }}>
        <CardHeader>
          <div>
            <h2>User Role Management</h2>
            <p>Assign roles for authenticated users. Allowed roles: admin, subadmin.</p>
          </div>
          <PrimaryButton type="button" onClick={handleOpenModal}>
            Create user
          </PrimaryButton>
        </CardHeader>

        {users.length === 0 ? (
          <Message>No users found. Users appear here after first login.</Message>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>UID</th>
                  <th>Role</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isOwnAccount = user.id === authUser?.uid;
                  return (
                    <tr key={user.id}>
                      <td data-label="Email">{user.email || "-"}</td>
                      <td data-label="UID">{user.id}</td>
                      <td data-label="Role">
                        <RoleSelect
                          value={user.role || ""}
                          disabled={savingUid === user.id || isOwnAccount}
                          onChange={(event) => updateRole(user.id, event.target.value)}
                          style={{ width: "100%", maxWidth: "200px" }}
                        >
                          <option value="">No role</option>
                          <option value={ROLES.ADMIN}>Admin</option>
                          <option value={ROLES.SUB_ADMIN}>Sub Admin</option>
                        </RoleSelect>
                      </td>
                      <td data-label="Actions">
                        <DeleteUserButton
                          type="button"
                          title="Delete user profile"
                          disabled={
                            isOwnAccount ||
                            deletingUid === user.id ||
                            savingUid === user.id
                          }
                          onClick={() => deleteUserProfile(user.id, user.email)}
                        >
                          <TrashIcon size={18} aria-hidden />
                        </DeleteUserButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Wrapper>
  );
}
